import React, {useEffect, useState} from "react";
import {getCurrentUser, updatePassword} from "../../services/auth.service";
import {Box, Breadcrumbs, Button, Card, CardContent, Grid, Stack, Typography} from "@mui/joy";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import Avatar from "@mui/joy/Avatar";
import IUser from "../../types/user.type";
import {getById, getProfileByUserId} from "../../services/user.service";
import ChangePasswordModal from "./ChangePasswordModal";
import IUpdatePassword from "../../types/update-password.type";
import ChangeProfileImageModal from "./ChangeProfileImageModal";
import PersonalDataForm from "./PersonalDataForm";
import AddressDataForm from "./AddressDataForm";
import BankDataForm from "./BankDataForm";
import EmploymentDataForm from "./EmploymentDataForm";
import IProfile from "../../types/profile.type";
import RelativesDataForm from "./RelativesDataForm";
import showNotification from "../utils/Notify";
import {uploadProfileFile} from "../../services/profile.service";
import {ProfileFileNames} from "../../types/enumeration/ProfileFileNames";
import FileDataForm from "./FileDataForm";

const ProfileView: React.FC = () => {
  const currentUser = getCurrentUser();
  const [user, setUser] = useState<IUser>();
  const [profile, setProfile] = useState<IProfile>();
  const [changePasswordModalOpen, setChangePasswordModalOpen] = React.useState(false);
  const [changeProfileImageModalOpen, setChangeProfileImageModalOpen] = React.useState(false);

  //Change password modal methods
  const handleChangePasswordModalClose = () => {
    setChangePasswordModalOpen(false);
  }

  const handleChangePasswordClick = () => {
    setChangePasswordModalOpen(true);
  };

  const onSubmitChangePassword = (password: IUpdatePassword) => {
    updatePassword(password)
      .then((response) => {
        showNotification({type: 'success', message: 'Contraseña actualizada correctamente.'});
        handleChangePasswordModalClose();
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  }

  //Change profile modal methods
  const handleChangeProfileImageModalClose = () => {
    setChangeProfileImageModalOpen(false);
  }

  const handleChangeProfileImageClick = () => {
    setChangeProfileImageModalOpen(true);
  };

  const onSubmitChangeProfileImage = (data: { file: File | null }) => {
    if (data.file) {
      uploadProfileFile(profile?.id, data.file, ProfileFileNames.PROFILE_IMAGE)
        .then(() => {
          setChangeProfileImageModalOpen(false);
          showNotification({type: 'success', message: 'Imagen de perfil actualizada correctamente.'});
          fetchProfile()
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  }

  const fetchProfile = async () => {
    if (currentUser.id) {
      const profileResponse = await getProfileByUserId(currentUser.id);
      if (profileResponse.status != 204) {
        setProfile( profileResponse.data);
      } else {
        setProfile(undefined);
      }
    }
  };

  const fetchUser = async () => {
    if (currentUser.id) {
      const response = await getById(currentUser.id);
      setUser(response.data);
    }
  };

  useEffect(() => {
    fetchUser();
    fetchProfile();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <AccountBoxIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Perfil
              </Typography>
            </Breadcrumbs>
          </Box>

          <ChangePasswordModal
            isOpen={changePasswordModalOpen}
            onSubmit={onSubmitChangePassword}
            onClose={handleChangePasswordModalClose}
            userId={user?.id}
          />

          <ChangeProfileImageModal
            isOpen={changeProfileImageModalOpen}
            onSubmit={onSubmitChangeProfileImage}
            onClose={handleChangeProfileImageModalClose}
            preselectedImage={profile?.profileImage ? profile?.profileImage.filePath : null}
          />

          <Box sx={{ width: '100%', typography: 'body1'}} ml={1} mt={0} mb={2} pr={2}>
            <Card variant="outlined" sx={{ width: '100%', marginBottom: 2}}>
              <CardContent>
                <Grid container spacing={2} sx={{ flexGrow: 1 }} columns={{ xs: 12, sm: 12, md: 12, xl: 12 }}>
                  <Grid xl={8} md={8} sm={8} xs={12}>
                    <Stack direction="row" spacing={2} >

                      <Avatar
                        src={profile?.profileImage ? `${profile.profileImage.filePath}?t=${new Date().getTime()}` : "/static/images/avatar/1.jpg"}
                        sx={{height: 100, width: 100}}
                        size="lg"
                      />


                      <Stack spacing={1} pt={1}>
                        <Typography level="body-sm">
                          <b><u>Nombre de Usuario:</u></b> {user?.username}
                        </Typography>
                        <Typography level="body-sm">
                          <b><u>Email:</u></b> {user?.email}
                        </Typography>
                        <Typography level="body-sm">
                          <b><u>Rol:</u></b> {user?.roleName}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid xl={4} md={4} sm={4} xs={12}>
                    <Stack spacing={2} justifyContent="center" pt={1}>
                      <Button variant="outlined" color="neutral" onClick={handleChangeProfileImageClick}>
                        Cambiar Imagen de Perfil
                      </Button>
                      <Button variant="solid" color="primary" onClick={handleChangePasswordClick}>
                        Cambiar Contraseña
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <PersonalDataForm userId={currentUser.id} profile={profile} />

            <AddressDataForm userId={currentUser.id} profile={profile} />

            <BankDataForm userId={currentUser.id} profile={profile} />

            <RelativesDataForm userId={currentUser.id} profile={profile} />

            <EmploymentDataForm userId={currentUser.id} profile={profile} canEdit={false} />

            <FileDataForm
              userId={currentUser.id}
              profile={profile}
              profileFile={profile?.curriculum}
              profileFileName={ProfileFileNames.CURRICULUM}
              title={"Curriculum Vitae"}
              onProfileUpdated={(p) => setProfile(p)}
            />

            <FileDataForm
              userId={currentUser.id}
              profile={profile}
              profileFile={profile?.medicalExam}
              profileFileName={ProfileFileNames.MEDICAL_EXAM}
              title={"Examen Medico"}
              onProfileUpdated={(p) => setProfile(p)}
            />

            <FileDataForm
              userId={currentUser.id}
              profile={profile}
              profileFile={profile?.epp}
              profileFileName={ProfileFileNames.EPP}
              title={"EPP"}
              onProfileUpdated={(p) => setProfile(p)}
            />

            <FileDataForm
              userId={currentUser.id}
              profile={profile}
              profileFile={profile?.dni}
              profileFileName={ProfileFileNames.DNI}
              title={"DNI"}
              onProfileUpdated={(p) => setProfile(p)}
            />

            <FileDataForm
              userId={currentUser.id}
              profile={profile}
              profileFile={profile?.cuil}
              profileFileName={ProfileFileNames.CUIL}
              title={"CUIL"}
              onProfileUpdated={(p) => setProfile(p)}
            />

            <FileDataForm
              userId={currentUser.id}
              profile={profile}
              profileFile={profile?.drivingLicense}
              profileFileName={ProfileFileNames.DRIVING_LICENCE}
              title={"Licencia de Conducir"}
              onProfileUpdated={(p) => setProfile(p)}
            />

            <FileDataForm
              userId={currentUser.id}
              profile={profile}
              profileFile={profile?.passport}
              profileFileName={ProfileFileNames.PASSPORT}
              title={"Pasaporte"}
              onProfileUpdated={(p) => setProfile(p)}
            />

          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileView;
